/**
 * mergeDocumentIntoProxy
 *
 * Deep-merges a Firestore `data()` payload into a live Valtio document slot,
 * mutating only changed paths to preserve fine-grained reactivity. Consumers:
 * persistDocument, persistCollection, persistCollectionGroup, useArTableAggregations.
 * `docId` is synthetic — never present in `data()` — and is injected here so the
 * mirror-delete pass cannot strip it. That delete pass is recursive and
 * intentionally removes nested keys absent from source, including nested fields
 * named `docId`, so server-side field deletion propagates.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Check if value is a plain object (not null, array, Date, Timestamp, etc.)
 */
const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  val !== null &&
  typeof val === 'object' &&
  !Array.isArray(val) &&
  !(val instanceof Date) &&
  !(val instanceof Timestamp) &&
  Object.getPrototypeOf(val) === Object.prototype;

/**
 * Merge one level of source into target, recursing into plain objects and
 * deleting target keys absent from source.
 *
 * Preserves existing Timestamp values when incoming is null (handles
 * serverTimestamp() pending writes without corrupting conflict resolution).
 */
const mergeLevel = (target: Record<string, unknown>, source: Record<string, unknown>): void => {
  for (const key of Object.keys(source)) {
    const sourceVal = source[key];
    const targetVal = target[key];

    /* Preserve existing Timestamp if incoming is null (serverTimestamp pending write) */
    if (sourceVal === null && targetVal instanceof Timestamp) {
      continue;
    }

    /* Preserve Timestamp identity when value is unchanged. Each Firestore
     * onSnapshot creates fresh Timestamp instances even for unchanged fields;
     * writing them would invalidate Valtio tracked paths and re-render every
     * consumer (e.g. all fields' meta.lastModified) on any sibling field write. */
    if (
      sourceVal instanceof Timestamp &&
      targetVal instanceof Timestamp &&
      sourceVal.isEqual(targetVal)
    ) {
      continue;
    }

    if (isPlainObject(sourceVal) && isPlainObject(targetVal)) {
      if (Object.isFrozen(targetVal)) {
        target[key] = sourceVal;
      } else {
        try {
          mergeLevel(targetVal, sourceVal);
        } catch {
          target[key] = sourceVal;
        }
      }
    } else if (!Object.is(targetVal, sourceVal)) {
      target[key] = sourceVal;
    }
  }

  for (const key of Object.keys(target)) {
    if (!(key in source)) {
      delete target[key];
    }
  }
};

/**
 * Deep-merge a Firestore document payload into a live proxy slot.
 *
 * @param target - The Valtio proxy object to mutate
 * @param data - The incoming Firestore `data()` payload
 * @param docId - The document id, injected into the merge source
 */
export const mergeDocumentIntoProxy = (
  target: Record<string, unknown>,
  data: Record<string, unknown>,
  docId: string
): void => {
  mergeLevel(target, { ...data, docId });
};
